import { Box, Paper, Typography } from "@material-ui/core";
import { ArrowForward, FiberManualRecord } from "@material-ui/icons";
import { useEventCardStyles } from "@saleor/auditLogs/utils/eventCardStyles";
import {
  convertStringDeliveryType,
  isStatusFieldUpdated
} from "@saleor/auditLogs/utils/utils";
import React from "react";

import { ShippingZoneActivityLogs_shippingZoneActivityLogs_edges_node } from "../../types/ShippingZoneActivityLogs";
import {
  getChangedLocationRangeFields,
  hasKmRangeChanged,
  hasRateNameChanged,
  hasRateNameChangedFromParams,
  hasStatusChanged,
  isLocationRateUpdate,
  LocationRateUpdate
} from "../../utils/locationRateUtils";
import EventCardHeader from "../EventCardHeader";
import SeeMoreSeeLess from "../SeeMoreSeeLess";
import TimeContainer from "../TimeContainer";

interface LocationRateEventCardProps {
  event: ShippingZoneActivityLogs_shippingZoneActivityLogs_edges_node;
  eventNewValues: LocationRateUpdate;
}

enum CHANGED_VALUE_ENUM {
  OTHERS = 0,
  PRICE_ONLY = 1,
  LOCATION_RANGE = 2
}

const LocationRateEventCard: React.FC<LocationRateEventCardProps> = props => {
  const { event, eventNewValues } = props;
  const classes = useEventCardStyles({});

  const [showActivity, setShowActivity] = React.useState(true);
  const [spliceLength, setSpliceLength] = React.useState(3);

  let deliveryType = "";
  let eventName = "";
  let eventStatus = "";
  let eventParameters: any[] = [];

  if (isLocationRateUpdate(eventNewValues)) {
    deliveryType = eventNewValues.deliveryType
      ? convertStringDeliveryType(eventNewValues.deliveryType)
      : "";

    if (eventNewValues && eventNewValues?.parameters) {
      const filteredEventParams = eventNewValues?.parameters.filter(
        param => JSON.stringify(param.old) !== JSON.stringify(param.new)
      );
      eventParameters = filteredEventParams || [];
    }

    const statusWasChanged = isStatusFieldUpdated(eventParameters);
    if (
      statusWasChanged ||
      event.action.includes("DELETE") ||
      event.action.includes("CREATE")
    ) {
      eventStatus = event.action.includes("DELETE")
        ? "DELETED"
        : eventNewValues?.newStatus === "ACT"
        ? "ACTIVE"
        : eventNewValues?.newStatus;
    }

    const rateNameWasChanged = hasRateNameChangedFromParams(eventParameters);
    eventName = rateNameWasChanged
      ? deliveryType
      : deliveryType + " (" + eventNewValues?.rateName + ")";
  }

  const eventStatusClass =
    eventStatus === "ACTIVE"
      ? `${classes.eventStatus} ${classes.eventStatusGreen}`
      : eventStatus === "DELETED"
      ? `${classes.eventStatus} ${classes.eventStatusRed}`
      : classes.eventStatus;

  const formatDecimalValue = (value: string) => parseFloat(value).toString();

  const getChangedValue = parameter => {
    const changed = getChangedLocationRangeFields(parameter.old, parameter.new);

    if (changed.length === 1 && changed[0] === "priceAmount") {
      return CHANGED_VALUE_ENUM.PRICE_ONLY;
    }
    if (
      !changed.includes("priceAmount") &&
      (changed.includes("fromKm") || changed.includes("toKm"))
    ) {
      return CHANGED_VALUE_ENUM.LOCATION_RANGE;
    }

    return CHANGED_VALUE_ENUM.OTHERS;
  };

  const getLocationRateDisplay = (parameter, displayComparison = false) => {
    if (parameter?.action.includes("delete")) {
      return (
        <>
          {formatDecimalValue(parameter.old.fromKm) || ""}-
          {formatDecimalValue(parameter.old.toKm) || ""} km
        </>
      );
    } else if (displayComparison) {
      return (
        <>
          <span className={classes.textGray}>
            {formatDecimalValue(parameter.old.fromKm) || ""}-
            {formatDecimalValue(parameter.old.toKm) || ""} km
          </span>{" "}
          <ArrowForward className={classes.rowArrowIcon} />{" "}
          {formatDecimalValue(parameter.new.fromKm) || ""}-
          {formatDecimalValue(parameter.new.toKm) || ""} km
        </>
      );
    } else {
      return (
        <>
          {formatDecimalValue(parameter.new.fromKm) || ""}-
          {formatDecimalValue(parameter.new.toKm) || ""} km
        </>
      );
    }
  };

  const getPriceAmountDisplay = (parameter, displayComparison = false) => {
    if (parameter?.action.includes("delete")) {
      return <>PHP {parameter?.old?.priceAmount || ""}</>;
    } else if (displayComparison) {
      return (
        <>
          <span className={classes.textGray}>
            PHP {parameter?.old?.priceAmount || ""}{" "}
          </span>{" "}
          <ArrowForward className={classes.rowArrowIcon} /> PHP{" "}
          {parameter?.new?.priceAmount || ""}
        </>
      );
    } else {
      return <>PHP {parameter?.new?.priceAmount || ""}</>;
    }
  };

  const parseStatus = (str: string) =>
    str.toUpperCase() === "ACT" ? "ACTIVE" : str;

  const isStatusFieldPresent = (param): boolean =>
    "status" in (param.old || {}) || "status" in (param.new || {});

  const showEventItems =
    eventParameters.length > 0 &&
    (eventParameters.length > 1 ||
      (eventParameters.length === 1 &&
        !isStatusFieldPresent(eventParameters[0])));

  return (
    <Box className={classes.timelineBox}>
      <Box className={classes.timelineContainer}>
        <FiberManualRecord className={`${classes.dot} [status]`} />
        <Box className={classes.timelineItem}>
          <TimeContainer
            dateTimeStamp={event.createdAt}
            eventStatus={eventStatus}
            eventStatusClass={eventStatusClass}
            showDot={eventStatus === "ACTIVE" || eventStatus === "INACTIVE"}
          />

          <div style={{ width: "100%" }}>
            <EventCardHeader
              eventName={eventName}
              eventUser={event?.doer?.email}
              eventUserActionType={
                event.action.includes("CREATE") ? "created by" : "updated by"
              }
              showEventItems={showEventItems}
              showActivity={showActivity}
              action={() => setShowActivity(!showActivity)}
            />

            {showActivity && (
              <Paper className={classes.paper}>
                {showEventItems &&
                  eventParameters
                    .slice(0, spliceLength)
                    .map((parameter, index) => {
                      const changedValue = getChangedValue(parameter);

                      return (
                        <div
                          key={`auditLogsLocationEventItemCard-${index}`}
                          className={`${classes.updateRow} ${
                            (index + 1) % 2 === 1 ? classes.rowBg : ""
                          }`}
                        >
                          {parameter?.action.includes("delete") && (
                            <div
                              className={`${classes.rowBadge} ${classes.rowBadgeError}`}
                            >
                              Deleted
                            </div>
                          )}

                          {event.action.includes("UPDATE") &&
                            parameter?.action.includes("create") && (
                              <div
                                className={`${classes.rowBadge} ${classes.rowBadgePrimary}`}
                              >
                                Created
                              </div>
                            )}

                          {parameter?.action.includes("update") &&
                          changedValue === CHANGED_VALUE_ENUM.PRICE_ONLY ? (
                            <div
                              className={`${classes.rowBadge} ${classes.rowBadgeInfo}`}
                            >
                              {getLocationRateDisplay(parameter)}
                            </div>
                          ) : parameter?.action.includes("update") &&
                            changedValue ===
                              CHANGED_VALUE_ENUM.LOCATION_RANGE ? (
                            <div
                              className={`${classes.rowBadge} ${classes.rowBadgeInfo}`}
                            >
                              {getPriceAmountDisplay(parameter)}
                            </div>
                          ) : (
                            <></>
                          )}

                          <Typography>
                            <ul>
                              {hasRateNameChanged(parameter) && (
                                <li>
                                  <div className={classes.rowLi}>
                                    <span className={classes.rowLabel}>
                                      Rate Name:
                                    </span>

                                    {parameter?.action.includes("update") ? (
                                      <>
                                        <span className={classes.textGray}>
                                          {parameter?.old?.name}
                                        </span>{" "}
                                        <ArrowForward
                                          className={classes.rowArrowIcon}
                                        />{" "}
                                        {parameter?.new?.name}
                                      </>
                                    ) : (
                                      <>{parameter?.new?.rateName}</>
                                    )}
                                  </div>
                                </li>
                              )}

                              {hasStatusChanged(parameter) && (
                                <li>
                                  <div className={classes.rowLi}>
                                    <span className={classes.rowLabel}>
                                      Status:
                                    </span>

                                    {parameter?.action.includes("update") ? (
                                      <>
                                        <span className={classes.textGray}>
                                          {parseStatus(parameter?.old?.status)}
                                        </span>{" "}
                                        <ArrowForward
                                          className={classes.rowArrowIcon}
                                        />{" "}
                                        {parseStatus(parameter?.new?.status)}
                                      </>
                                    ) : (
                                      <>{parseStatus(parameter?.new?.status)}</>
                                    )}
                                  </div>
                                </li>
                              )}

                              {hasKmRangeChanged(parameter) && (
                                <>
                                  {parameter?.action.includes("update") &&
                                  changedValue ===
                                    CHANGED_VALUE_ENUM.PRICE_ONLY ? (
                                    <li>
                                      <div className={classes.rowLi}>
                                        <span className={classes.rowLabel}>
                                          Delivery Fee:
                                        </span>{" "}
                                        {getPriceAmountDisplay(parameter, true)}
                                      </div>
                                    </li>
                                  ) : parameter?.action.includes("update") &&
                                    changedValue ===
                                      CHANGED_VALUE_ENUM.LOCATION_RANGE ? (
                                    <li>
                                      <div className={classes.rowLi}>
                                        <span className={classes.rowLabel}>
                                          Location Range:
                                        </span>
                                        {getLocationRateDisplay(
                                          parameter,
                                          true
                                        )}
                                      </div>
                                    </li>
                                  ) : (
                                    <>
                                      <li>
                                        <div className={classes.rowLi}>
                                          <span className={classes.rowLabel}>
                                            Location Range:
                                          </span>

                                          {getLocationRateDisplay(
                                            parameter,
                                            parameter?.action.includes("update")
                                          )}
                                        </div>
                                      </li>
                                      <li>
                                        <div className={classes.rowLi}>
                                          <span className={classes.rowLabel}>
                                            Delivery Fee:
                                          </span>{" "}
                                          {getPriceAmountDisplay(
                                            parameter,
                                            parameter?.action.includes("update")
                                          )}
                                        </div>
                                      </li>
                                    </>
                                  )}
                                </>
                              )}
                            </ul>
                          </Typography>
                        </div>
                      );
                    })}

                {eventParameters.length > 3 && (
                  <SeeMoreSeeLess
                    totalLength={eventParameters.length}
                    spliceLength={spliceLength}
                    setSpliceLength={setSpliceLength}
                  />
                )}
              </Paper>
            )}
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default LocationRateEventCard;
